import {
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
} from 'reactstrap';
import { DegreeType } from '../../common/Data';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import { Button } from '../../components';
import MultiSelect from 'react-multi-select-component';

type Option = {
    label: string;
    value: string;
};

interface AccreditedProgramsFormData {
    professionId?: number;
    professionLevelId?: number;
    selectedStatus: Option[];
    effectiveDate?: string;
    asOfEffectiveDate?: string;
    initialAccreditationDate?: string;
    institutionId?: number;
    institutionTypeId?: number;
    programName?: string;
    selectedDegreeType: Option[];
    selectedAuspices: Option[];
    city?: string;
    selectedStates: Option[];
    distance: boolean;
}
import { ProgramStatus, States } from '../../common/Data';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styled from 'styled-components';
import {
    AccreditedProgramDto,
    AuspiceDto,
    Institution,
    InstitutionTypeDto,
    ProfessionDto,
    ProfessionLevel,
} from '../../common/Types';
import { professionService } from '../../services/ProfessionAdministration';
import { institutionService } from '../../services/InstitutionService';
import { programService } from '../../services/ProgramService';
import { reportService } from '../../services/ReportService';
import moment from 'moment';
import { PagingDto } from '../../types';

const StyledMultiSelect = styled(MultiSelect)`
    width: 300px;

    & .dropdown-container {
        width: 100%;
        max-width: 100%;
    }

    & .dropdown-content {
        z-index: 3;
        border: none !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;

        /* Force all child elements to have minimal left padding */
        & > * {
            padding-left: 0.25rem !important;
        }
    }

    & .select-item {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;

        /* Ensure selection background spans full width */
        &.selected {
            background-color: rgba(13, 110, 253, 0.1) !important;
            width: 100% !important;
            margin-left: 0 !important;
            padding-left: 0.5rem !important;
        }

        &:hover {
            background-color: rgba(0,0,0,0.05);
        }

        /* Force all children to stay in row */
        > * {
            display: inline-flex !important;
            align-items: center !important;
        }
    }

    /* Force label to be flex row */
    & .select-item label {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        margin: 0;
        cursor: pointer;
    }

    /* Keep checkboxes inline with proper spacing */
    & .select-item input[type="checkbox"] {
        display: inline-block !important;
        margin-right: 0.5rem !important;
        vertical-align: middle !important;
        flex-shrink: 0;
    }

    /* Ensure text stays inline */
    & .select-item span,
    & .select-item label span {
        display: inline-block !important;
        vertical-align: middle !important;
    }
`;
const AccreditedProgramsReport = () => {
    const isMounted = useRef(true);
    const [total, setTotal] = useState(0);
    const [professionId, setProfessionId] = useState<number | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<Option[]>([]);
    const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
    const [asOfEffectiveDate, setAsOfEffectiveDate] = useState<Date | null>(null);
    const [initialAccreditationDate, setInitialAccreditationDate] = useState<Date | null>(null);
    const [institutionId, setInstitutionId] = useState<number | null>(null);
    const [institutionTypeId, setInstitutionTypeId] = useState<number | null>(null);
    const [programName, setProgramName] = useState<string | undefined>(undefined);
    const [selectedDegreeType, setSelectedDegreeType] = useState<Option[]>([]);
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [auspices, setAuspices] = useState<AuspiceDto[]>([]);
    const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeDto[]>([]);
    const [selectedAuspices, setSelectedAuspices] = useState<Option[]>([]);
    const [city, setCity] = useState<string | undefined>(undefined);
    const [selectedStates, setSelectedStates] = useState<Option[]>([]);
    const [distance, setDistance] = useState<boolean>(false);
    const [programNames, setProgramNames] = useState<string[]>([]);
    const [professions, setProfessions] = useState<ProfessionDto[]>([]);
    const [accreditedPrograms, setAccreditedPrograms] = useState<AccreditedProgramDto[]>([]);
    const [levels, setLevels] = useState<ProfessionLevel[]>([]);
    const [filteredLevels, setFilteredLevels] = useState<ProfessionLevel[]>([]);
    const [professionLevelId, setProfessionLevelId] = useState<number | null>(null);
    const [paging, setPaging] = useState<PagingDto>({
        pageNumber: 1,
        maxPageSize: null,
        pageSize: 10,
    });

    // Memoize validation schema
    const validationSchema = useMemo(() => {
        return yup.object({
            professionId: yup.number().optional(),
            professionLevelId: yup.number().optional(),
            selectedStatus: yup.array().notRequired(),
            effectiveDate: yup.string().optional(),
            asOfEffectiveDate: yup.string().optional(),
            initialAccreditationDate: yup.string().optional(),
            institutionId: yup.number().optional(),
            institutionTypeId: yup.number().optional(),
            programName: yup.string().optional(),
            selectedDegreeType: yup.array().notRequired(),
            selectedAuspices: yup.array().notRequired(),
            city: yup.string().optional(),
            selectedStates: yup.array().notRequired(),
            distance: yup.boolean().default(false),
        });
    }, []);

    // Memoize default values
    const defaultValues = useMemo(
        (): AccreditedProgramsFormData => ({
            professionId: undefined,
            professionLevelId: undefined,
            selectedStatus: [],
            effectiveDate: undefined,
            asOfEffectiveDate: undefined,
            initialAccreditationDate: undefined,
            institutionId: undefined,
            institutionTypeId: undefined,
            programName: undefined,
            selectedDegreeType: [],
            selectedAuspices: [],
            city: undefined,
            selectedStates: [],
            distance: false,
        }),
        [],
    );

    const {
        control,
        handleSubmit,
        register,
        reset,
        formState: { errors, isDirty },
    } = useForm<AccreditedProgramsFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
        defaultValues,
    });

    useEffect(() => {
        const loadProfessions = () => {
            professionService
                .getProfessions()
                .then((results) => {
                    if (isMounted.current) {
                        setProfessions([
                            ...results
                                .filter((p) => p.professionId === 32 || !p.isDeleted)
                                .sort((a, b) => a.title.localeCompare(b.title)),
                        ]);
                    }
                })
                .catch(() => {
                    toast.error('Error loading professions');
                });
        };
        loadProfessions();
    }, [setProfessions]);

    useEffect(() => {
        professionService
            .getAllLevels()
            .then((response) => {
                if (isMounted.current) {
                    setLevels(response);
                    setFilteredLevels(response);
                }
            })
            .catch(() => {
                toast.error('Failed to load levels.');
            });
    }, [setLevels, setFilteredLevels]);

    useEffect(() => {
        const loadInstitutions = () => {
            institutionService
                .getInstitutions()
                .then((response) => {
                    if (isMounted.current) {
                        setInstitutions(response);
                    }
                })
                .catch(() => {
                    toast.error('Error loading institutions');
                });
        };
        const loadAuspices = () => {
            institutionService
                .getAuspice()
                .then((results) => {
                    if (isMounted.current) {
                        setAuspices([
                            ...(results
                                .filter((r) => r.isActive === true)
                                .sort((a, b) => a.title.localeCompare(b.title)) || []),
                        ]);
                    }
                })
                .catch(() => {
                    toast.error('Error loading auspices');
                });
        };
        const loadInstitutionTypes = () => {
            institutionService
                .getTypes()
                .then((results) => {
                    if (isMounted.current) {
                        setInstitutionTypes([
                            ...(results
                                .filter((r) => r.isActive === true)
                                .sort((a, b) => a.title.localeCompare(b.title)) || []),
                        ]);
                    }
                })
                .catch(() => {
                    toast.error('Error loading institution types');
                });
        };
        loadInstitutionTypes();
        loadInstitutions();
        loadAuspices();
    }, [setInstitutions, setAuspices, setInstitutionTypes]);

    useEffect(() => {
        programService
            .getProgramNames()
            .then((response) => {
                if (isMounted.current) {
                    setProgramNames(response);
                }
            })
            .catch(() => toast.error('Failed to get program names'));
    }, [setProgramNames]);

    useEffect(() => {
        const toastId = toast.info('Searching...', {
            autoClose: false,
        });
        const getData = () => {
            reportService
                .getAccreditedPrograms(
                    paging,
                    professionId,
                    selectedStatus?.map((s) => Number(s['value'])),
                    effectiveDate ? moment(effectiveDate).format() : effectiveDate,
                    asOfEffectiveDate ? moment(asOfEffectiveDate).format() : asOfEffectiveDate,
                    initialAccreditationDate ? moment(initialAccreditationDate).format() : initialAccreditationDate,
                    institutionId,
                    institutionTypeId,
                    programName,
                    selectedDegreeType?.map((d) => Number(d['value'])),
                    selectedAuspices?.map((a) => Number(a['value'])),
                    city,
                    selectedStates?.map((s) => s['value']),
                    distance,
                    professionLevelId,
                )
                .then((response) => {
                    if (isMounted.current) {
                        setAccreditedPrograms(response.records);
                        setTotal(response.total);
                        toast.update(toastId, {
                            type: 'success',
                            autoClose: 500,
                            closeOnClick: true,
                            draggable: true,
                        });
                    }
                })
                .catch(() => toast.error('Failed to get accredited programs'));
        };

        getData();
    }, [
        setAccreditedPrograms,
        paging,
        professionId,
        selectedStatus,
        effectiveDate,
        asOfEffectiveDate,
        initialAccreditationDate,
        institutionId,
        institutionTypeId,
        programName,
        selectedDegreeType,
        selectedAuspices,
        city,
        selectedStates,
        distance,
        professionLevelId,
    ]);

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const clearSearchForm = useCallback(() => {
        setProfessionId(null);
        setProfessionLevelId(null);
        setSelectedStatus([]);
        setEffectiveDate(null);
        setAsOfEffectiveDate(null);
        setInitialAccreditationDate(null);
        setInstitutionId(null);
        setInstitutionTypeId(null);
        setProgramName(undefined);
        setSelectedDegreeType([]);
        setSelectedAuspices([]);
        setCity(undefined);
        setSelectedStates([]);
        setDistance(false);
        setFilteredLevels(levels);
        setPaging({
            pageNumber: 1,
            maxPageSize: null,
            pageSize: 10,
        });
        reset(defaultValues);
    }, [levels, reset, defaultValues]);

    const filterLevels = useCallback(
        (professionId: number | null) => {
            if (professionId && professionId > 0) {
                setFilteredLevels(levels.filter((l) => l.professionId === professionId));
            } else {
                setFilteredLevels(levels);
            }
        },
        [levels],
    );

    const onSubmit = useCallback((values: AccreditedProgramsFormData) => {
        // Form submission is handled by useEffect when dependencies change
        // This function exists to satisfy the form's onSubmit requirement
    }, []);

    const exportReport = useCallback(() => {
        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });
        reportService
            .exportAccreditedProgramReport(
                professionId,
                selectedStatus?.map((s) => Number(s.value)),
                effectiveDate ? moment(effectiveDate).format() : null,
                asOfEffectiveDate ? moment(asOfEffectiveDate).format() : null,
                initialAccreditationDate ? moment(initialAccreditationDate).format() : null,
                institutionId,
                institutionTypeId,
                programName,
                selectedDegreeType?.map((d) => Number(d.value)),
                selectedAuspices?.map((a) => Number(a.value)),
                city,
                selectedStates?.map((s) => s.value),
                distance,
                professionLevelId,
            )
            .then((report) => {
                if (report) {
                    const url = window.URL.createObjectURL(report);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'AccreditedProgramReport.xlsx';
                    a.click();
                    toast.update(toastId, {
                        render: 'Report generated successfully.',
                        type: 'success',
                        autoClose: 2500,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Unable to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, [
        professionId,
        selectedStatus,
        effectiveDate,
        asOfEffectiveDate,
        initialAccreditationDate,
        institutionId,
        institutionTypeId,
        programName,
        selectedDegreeType,
        selectedAuspices,
        city,
        selectedStates,
        distance,
        professionLevelId,
    ]);

    return (
        <>
            <Card>
                <CardBody>
                    <Row>
                        <Col>
                            <div className="mb-3">
                                <h3>Accredited Programs Report</h3>
                            </div>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                {/* Main container */}
                                <div className="d-flex flex-column w-100" style={{ gap: '1.5rem' }}>
                                    
                                    {/* Profession Group */}
                                    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '1rem' }}>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="professionId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Profession
                                            </Label>
                                            <Controller
                                                name="professionId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="select"
                                                        {...field}
                                                        style={{ width: '300px' }}
                                                        value={field.value || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value ? Number(e.target.value) : null;
                                                            field.onChange(value);
                                                            setProfessionId(value);
                                                            filterLevels(value);
                                                        }}
                                                    >
                                                        <option value="">Select...</option>
                                                        {professions?.map((p) => (
                                                            <option key={p.professionId} value={p.professionId}>
                                                                {p.title}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                )}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="professionLevelId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Profession Level
                                            </Label>
                                            <Controller
                                                name="professionLevelId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="select"
                                                        {...field}
                                                        style={{ width: '300px' }}
                                                        value={field.value || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value ? Number(e.target.value) : null;
                                                            field.onChange(value);
                                                            setProfessionLevelId(value);
                                                        }}
                                                    >
                                                        <option value="">Select...</option>
                                                        {filteredLevels?.map((p) => (
                                                            <option key={p.id} value={p.id}>
                                                                {p.title}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                )}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="programName" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Profession:
                                            </Label>
                                            <Input
                                                type="select"
                                                {...register('programName')}
                                                style={{ width: '200px' }}
                                                onChange={(e) => setProgramName(e.target.value)}
                                            >
                                                <option value={''}>Select...</option>
                                                {programNames?.map((p) => (
                                                    <option key={p} value={p}>
                                                        {p}
                                                    </option>
                                                ))}
                                            </Input>
                                        </div>
                                    </div>

                                    {/* Status and Options Group */}
                                    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '1rem' }}>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="status" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Status
                                            </Label>
                                            <StyledMultiSelect
                                                options={Object.entries(ProgramStatus)
                                                    .filter((p) => !isNaN(p[0] as any) && Number(p[0]) <= 13)
                                                    .map((p) => {
                                                        return {
                                                            label: String(p[1]),
                                                            value: String(p[0]),
                                                        };
                                                    })}
                                                disableSearch={true}
                                                hasSelectAll={false}
                                                value={selectedStatus}
                                                onChange={setSelectedStatus}
                                                labelledBy="Status"
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="degreeTypes" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Degree Types
                                            </Label>
                                            <StyledMultiSelect
                                                options={Object.entries(DegreeType)
                                                    .filter((d) => !isNaN(d[0] as any))
                                                    .map((d) => {
                                                        return {
                                                            label: String(d[1]),
                                                            value: String(d[0]),
                                                        };
                                                    })}
                                                disableSearch={true}
                                                hasSelectAll={false}
                                                value={selectedDegreeType}
                                                onChange={setSelectedDegreeType}
                                                labelledBy="Degree Types"
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <div className="custom-control custom-checkbox">
                                                <Controller
                                                    name="distance"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="checkbox"
                                                            id="distance"
                                                            name={field.name}
                                                            onBlur={field.onBlur}
                                                            ref={field.ref}
                                                            checked={field.value || false}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.checked);
                                                                setDistance(e.target.checked);
                                                            }}
                                                            className="custom-control-input"
                                                        />
                                                    )}
                                                />
                                                <Label for="distance" className="custom-control-label">
                                                    Distance
                                                </Label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Date Fields Group */}
                                    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '1rem' }}>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="effectiveDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Effective Date (Exact):
                                            </Label>
                                            <Controller
                                                name="effectiveDate"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="date"
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value || undefined);
                                                            setEffectiveDate(e.target.value ? new Date(e.target.value) : null);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="asOfEffectiveDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Effective Date (As Of):
                                            </Label>
                                            <Controller
                                                name="asOfEffectiveDate"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="date"
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value || undefined);
                                                            setAsOfEffectiveDate(
                                                                e.target.value ? new Date(e.target.value) : null,
                                                            );
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="initialAccreditationDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Initial Accreditation Date:
                                            </Label>
                                            <Controller
                                                name="initialAccreditationDate"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="date"
                                                        {...field}
                                                        value={field.value || ''}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value || undefined);
                                                            setInitialAccreditationDate(
                                                                e.target.value ? new Date(e.target.value) : null,
                                                            );
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {/* Institution Group */}
                                    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '1rem' }}>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="institutionId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Institution
                                            </Label>
                                            <Controller
                                                name="institutionId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="select"
                                                        {...field}
                                                        style={{ width: '300px' }}
                                                        value={field.value || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value ? Number(e.target.value) : null;
                                                            field.onChange(value);
                                                            setInstitutionId(value);
                                                        }}
                                                    >
                                                        <option value="">Select...</option>
                                                        {institutions?.map((i) => (
                                                            <option key={i.institutionId} value={i.institutionId}>
                                                                {i.title}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                )}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="institutionTypeId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                Institution Type:
                                            </Label>
                                            <Controller
                                                name="institutionTypeId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="select"
                                                        {...field}
                                                        style={{ width: '200px' }}
                                                        onChange={(e) => {
                                                            field.onChange(+e.target.value);
                                                            setInstitutionTypeId(
                                                                institutionTypes?.find((t) => t.institutionTypeId === +e.target.value)
                                                                    ?.institutionTypeId || null,
                                                            );
                                                        }}
                                                    >
                                                        <option value={''}>Select...</option>
                                                        {institutionTypes?.map((t) => (
                                                            <option key={t.institutionTypeId} value={t.institutionTypeId}>
                                                                {t.title}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                )}
                                            />
                                        </div>
                                        {auspices && auspices?.length > 0 && (
                                            <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                                <Label for="auspice" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                    Auspice
                                                </Label>
                                                <StyledMultiSelect
                                                    options={
                                                        auspices?.map((a) => {
                                                            return {
                                                                label: String(a.title),
                                                                value: String(a.auspiceID),
                                                            };
                                                        }) || []
                                                    }
                                                    disableSearch={true}
                                                    hasSelectAll={false}
                                                    value={selectedAuspices}
                                                    onChange={setSelectedAuspices}
                                                    labelledBy="Auspice"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Location Group */}
                                    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '1rem' }}>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="city" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                City
                                            </Label>
                                            <Controller
                                                name="city"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        {...field}
                                                        placeholder="City"
                                                        style={{ width: '200px' }}
                                                        value={field.value || ''}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value || undefined);
                                                            setCity(e.target.value || undefined);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Label for="state" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                                State
                                            </Label>
                                            <StyledMultiSelect
                                                options={
                                                    States?.map((s) => {
                                                        return {
                                                            label: String(s.name),
                                                            value: String(s.abbreviation),
                                                        };
                                                    }) || []
                                                }
                                                disableSearch={true}
                                                hasSelectAll={false}
                                                value={selectedStates}
                                                onChange={setSelectedStates}
                                                labelledBy="State"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Button row within the form */}
                                <Row className="mt-3">
                                <Col className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                        <Button
                                            type="submit"
                                            color="primary"
                                            onClick={handleSubmit(onSubmit)}
                                        >
                                            Search
                                        </Button>
                                        <Button
                                            type="button"
                                            color="link"
                                            onClick={clearSearchForm}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                    <Button
                                        type="button"
                                        color="info"
                                        onClick={exportReport}
                                        disabled={accreditedPrograms.length === 0}
                                    >
                                        Export Report
                                    </Button>
                                </Col>
                            </Row>
                            </Form>
                        </Col>
                    </Row>
                    <div className="mt-3">
                        <h4>Accredited Programs</h4>
                    </div>
                </CardBody>
                <Table>
                    <thead>
                        <tr>
                            <th>Profession</th>
                            <th></th>
                            <th>Program</th>
                            <th></th>
                            <th>Degree</th>
                            <th>Institution</th>
                            <th></th>
                            <th>Program City</th>
                            <th>Program State</th>
                            <th>Distance</th>
                            <th>Accreditation Status</th>
                            <th>Status Effective Date</th>
                            <th>Next Full Evaluation</th>
                            <th>Progress Report Due Date</th>
                            <th>Outcomes Url</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accreditedPrograms ? (
                            accreditedPrograms.map((program, i) => (
                                <tr key={program.programId ?? i}>
                                    <td>{program.professionTitle}</td>
                                    <td>
                                        {program.professionLevelTitle
                                            ? program.professionLevelTitle
                                            : program.concentrationTitle}
                                    </td>
                                    <td>{program.programId}</td>
                                    <td>{program.programName}</td>
                                    <td>{program.degree}</td>
                                    <td>{program.institutionId}</td>
                                    <td>{program.institutionName}</td>
                                    <td>{program.city}</td>
                                    <td>{program.state}</td>
                                    <td>{program.distance ? 'distance' : ''}</td>
                                    <td>{program.accreditationStatusTitle}</td>
                                    <td>
                                        {program.statusEffectiveDate &&
                                            new Date(program.statusEffectiveDate).toDateString()}
                                    </td>
                                    <td>{program.evaluationDate && new Date(program.evaluationDate).toDateString()}</td>
                                    <td>{program.prDue && new Date(program.prDue).toDateString()}</td>
                                    <td>{program.outcomesUrl}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={15}>No Results</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                <CardBody>
                    <h4>Total: {total}</h4>
                    <Pagination size="sm" aria-label="Accredited Programs Navigation">
                        <PaginationItem disabled={paging.pageNumber === 1}>
                            <PaginationLink
                                previous
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber - 1 })}
                            />
                        </PaginationItem>
                        <PaginationItem>
                            <PaginationLink
                                next
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber + 1 })}
                            />
                        </PaginationItem>
                    </Pagination>
                </CardBody>
            </Card>
        </>
    );
};

export default AccreditedProgramsReport;
